import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useStripeApi } from '../../hooks/useStripeApi';
import type { StripePayout } from '../../services/stripeApi';
import { listStripePayoutsResolved } from '../../services/stripeResolved';
import { fmtStripeMoney, fmtStripeUnix, payoutStatusColor, StripeHero, StripeStatCard, StripeStatusChip } from '../../components/stripe/stripeShared';

const StripePayoutsPage: React.FC = () => {
  const { settings, isSecretConfigured } = useStripeApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payouts, setPayouts] = useState<StripePayout[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();

  const [from, setFrom] = useState(searchParams.get('from') || format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [to, setTo] = useState(searchParams.get('to') || format(new Date(), 'yyyy-MM-dd'));

  const createdRange = useMemo(() => {
    const gte = Math.floor(new Date(from).getTime() / 1000);
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    const lte = Math.floor(toEnd.getTime() / 1000);
    return { gte, lte };
  }, [from, to]);

  const load = useCallback(
    async (append = false, after?: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listStripePayoutsResolved(settings, isSecretConfigured, {
          limit: 50,
          starting_after: after,
          createdGte: createdRange.gte,
          createdLte: createdRange.lte,
        });
        setPayouts((prev) => (append ? [...prev, ...res.data] : res.data));
        setHasMore(res.has_more);
        const last = res.data[res.data.length - 1];
        setCursor(last?.id);
      } catch (e) {
        const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed';
        setError(String(msg));
      } finally {
        setLoading(false);
      }
    },
    [settings, isSecretConfigured, createdRange]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const applyFilters = () => {
    const p = new URLSearchParams();
    if (from) {
      p.set('from', from);
    }
    if (to) {
      p.set('to', to);
    }
    setSearchParams(p, { replace: true });
    void load(false);
  };

  const summary = useMemo(() => {
    let paidCents = 0;
    let pendingCents = 0;
    let failed = 0;
    let currency = 'usd';
    for (const p of payouts) {
      currency = p.currency || currency;
      if (p.status === 'paid') {
        paidCents += p.amount;
      } else if (p.status === 'pending' || p.status === 'in_transit') {
        pendingCents += p.amount;
      } else if (p.status === 'failed') {
        failed += 1;
      }
    }
    return { count: payouts.length, paidCents, pendingCents, failed, currency };
  }, [payouts]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <StripeHero
          compact
          title="Stripe · Payouts"
          subtitle="Bank payouts when funds leave your Stripe balance — filter by created date."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load(false)} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<CheckCircleOutlineIcon />}
            label="Paid out"
            value={fmtStripeMoney(summary.paidCents, summary.currency)}
            sub="status=paid"
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<HourglassEmptyOutlinedIcon />}
            label="Pending / in transit"
            value={fmtStripeMoney(summary.pendingCents, summary.currency)}
            sub="Not yet in bank"
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<ErrorOutlineIcon />}
            label="Failed"
            value={String(summary.failed)}
            sub="In date range"
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<AccountBalanceOutlinedIcon />}
            label="Payouts shown"
            value={String(summary.count)}
            sub={`${from.slice(5)} → ${to.slice(5)}`}
            color="#635bff"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Arrival</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{fmtStripeMoney(p.amount, p.currency)}</TableCell>
                <TableCell>
                  <StripeStatusChip label={p.status} color={payoutStatusColor(p.status)} />
                </TableCell>
                <TableCell>{p.method || p.type || '—'}</TableCell>
                <TableCell>{p.arrival_date ? fmtStripeUnix(p.arrival_date) : '—'}</TableCell>
                <TableCell>{fmtStripeUnix(p.created)}</TableCell>
              </TableRow>
            ))}
            {!loading && payouts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No payouts in this date range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {hasMore && (
        <Button sx={{ mt: 2 }} disabled={loading} onClick={() => void load(true, cursor)}>
          {loading ? <CircularProgress size={22} /> : 'Load more'}
        </Button>
      )}
    </Box>
  );
};

export default StripePayoutsPage;
